import type { FC } from "react";

import { FieldError } from "@/shared/components/ui/FieldError";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { currencyInput } from "@/shared/utils/currencyInput";

export interface FuneralCostFormValues {
  funeralHomeName: string;
  amount: string;
  invoiceFile: File | null;
}

export const EMPTY_FUNERAL_COST_FORM: FuneralCostFormValues = {
  funeralHomeName: "",
  amount: "",
  invoiceFile: null,
};

export interface FuneralCostFieldErrors {
  funeralHomeName?: string;
}

export function validateFuneralCostFields(form: FuneralCostFormValues): FuneralCostFieldErrors {
  if (!form.funeralHomeName.trim())
    return { funeralHomeName: "Please enter the funeral home name" };
  return {};
}

export function funeralCostToItemFields(form: FuneralCostFormValues) {
  return {
    item_type: "Funeral Home Cost" as const,
    balance: parseFloat(form.amount) || 0,
  };
}

export function parseItemToForm(itemName: string, balance: number): FuneralCostFormValues {
  return {
    funeralHomeName: itemName,
    amount: balance ? String(balance) : "",
    invoiceFile: null,
  };
}

interface FuneralCostFormProps {
  form: FuneralCostFormValues;
  onChange: (form: FuneralCostFormValues) => void;
  fieldErrors?: FuneralCostFieldErrors;
  showUpload?: boolean;
  existingFileName?: string | null;
  onFileRemoved?: () => void;
}

export const FuneralCostForm: FC<FuneralCostFormProps> = ({
  form,
  onChange,
  fieldErrors,
  showUpload = true,
  existingFileName,
  onFileRemoved,
}) => {
  const amountInput = currencyInput(form.amount, (val) => onChange({ ...form, amount: val }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4">
        <div className="flex-1 flex-col gap-1.5">
          <Label className="mb-2 inline-block text-sm font-medium" required>
            Funeral home name
          </Label>
          <Input
            placeholder="e.g., Smith Funeral Home"
            value={form.funeralHomeName}
            onChange={(e) => onChange({ ...form, funeralHomeName: e.target.value })}
            className={fieldErrors?.funeralHomeName ? "border-error" : ""}
          />
          <FieldError message={fieldErrors?.funeralHomeName} className="mt-1" />
        </div>
        <div className="w-[160px] flex-col gap-1.5">
          <Label className="mb-2 inline-block text-sm font-medium">Amount</Label>
          <CurrencyInput
            value={amountInput.displayValue}
            onChange={amountInput.handleChange}
            placeholder="0.00"
          />
        </div>
      </div>

      {showUpload && (
        <UploadZone
          label="Paid invoice"
          labelSuffix="(optional)"
          file={form.invoiceFile}
          onFileSelect={(file) => onChange({ ...form, invoiceFile: file })}
          onFileRemove={() => {
            onChange({ ...form, invoiceFile: null });
            onFileRemoved?.();
          }}
          existingFileName={existingFileName}
        />
      )}
    </div>
  );
};
